<template>
  <AppLayout>
    <template #default>
      <h1 class="title">Professionnels de santé, accédez à votre cabinet médical connecté</h1>
      <h2 class="subtitle">Prise de RDV en ligne, agenda, téléconsultations, partage de documents, facturation en
        ligne...</h2>
      <form @submit.prevent="handleSubmit">
        <FormInput
          id="login-email"
          v-model.trim="email"
          autocomplete="username"
          label="Adresse email"
          type="email"
        >
          <template v-if="$v.email.$dirty" #error>
            <template v-if="!$v.email.required">Veuillez entrer votre adresse email</template>
            <template v-else-if="$v.email.$invalid">L'adresse email est invalide</template>
          </template>
        </FormInput>
        <FormInput
          id="login-password"
          v-model="password"
          :show-password="showPassword"
          autocomplete="current-password"
          class="password-input"
          label="Mot de passe"
          type="password"
          @toggle-password-visibility="showPassword = !showPassword"
        >
          <template v-if="$v.password.$dirty" #error>
            <template v-if="!$v.password.required">Veuillez entrer votre mot de passe</template>
            <template v-else-if="$v.password.$invalid">Le format du mot de passe est incorrect</template>
          </template>
        </FormInput>
        <router-link :to="{ name: 'forgotten-password' }" class="forgotten-password">Mot de passe oublié</router-link>
        <FormCheckbox v-model="rememberEmail" class="remember-email">Se souvenir de moi</FormCheckbox>
        <div class="submit">
          <ButtonGreen :busy="submitting" :icon="$icons.continue" :muted="!canBeSubmitted" type="submit">
            Continuer
          </ButtonGreen>
        </div>
      </form>
    </template>

    <template #footer>
      <div class="registration">
        <p>
          <router-link :to="{ name: 'signup' }">
            Pas encore inscrit&nbsp;?
          </router-link>
        </p>
        <p><a :href="$setting('URL_PATIENT_SIGNUP')">Je suis patient</a></p>
      </div>
    </template>
  </AppLayout>
</template>

<script>
  import { email, minLength, required } from '@byscripts/vuelidate/lib/validators';
  import AppLayout from '../components/AppLayout';
  import FormInput from '../components/Form/FormInput';
  import FormCheckbox from '../components/Form/FormCheckbox';
  import ButtonGreen from '../components/ButtonGreen';
  import { getAuthorizeURL, hasLocalStorage } from '@/helpers/tools';
  import { getLocalStorage, removeLocalStorage, setLocalStorage } from '@/helpers/tools.js';

  const LOCAL_STORAGE_KEY = 'LOGIN';

  export default {
    name: 'LoginPage',
    components: { ButtonGreen, FormCheckbox, FormInput, AppLayout },
    data() {
      return {
        showPassword: false,
        email: null,
        password: null,
        rememberEmail: false,
        submitting: false,
      };
    },
    validations: {
      email: {
        required,
        email,
      },
      password: {
        required,
        minLength: minLength(8),
      },
    },
    mounted() {
      if (hasLocalStorage(LOCAL_STORAGE_KEY)) {
        const { email } = getLocalStorage(LOCAL_STORAGE_KEY);

        if (email) {
          this.email = email;
          this.rememberEmail = true;
        }
      }

      if (typeof smartbanner === 'undefined') {
        document.addEventListener('smartbanner.init', () => {
          this.$nextTick(() => { smartbanner.publish(); });
        });
      } else {
        smartbanner.publish();
      }
    },
    beforeDestroy() {
      if (!smartbanner || !document.querySelector('.js_smartbanner')) {
        return;
      }

      const originalHideTtl = smartbanner.options.hideTtl;
      smartbanner.options.hideTtl = 1;

      smartbanner.exit();

      smartbanner.options.hideTtl = originalHideTtl;
    },
    computed: {
      canBeSubmitted() {
        return !this.$v.$invalid;
      },
    },
    methods: {
      async handleSubmit() {
        this.$v.$touch();

        if (this.$v.$invalid) {
          return this.$addError('Le formulaire contient des erreurs');
        }

        try {
          this.submitting = true;
          this.showPassword = false;

          // "Login" and "send auth code" are same endpoint
          const response = await this.$store.dispatch('authCode/sendCodeForLogin', {
            email: this.email,
            password: this.password,
          });

          if (this.rememberEmail) {
            setLocalStorage(LOCAL_STORAGE_KEY, { email: this.email });
          } else {
            removeLocalStorage(LOCAL_STORAGE_KEY);
          }

          if (response.checkPhoneCode) {
            await this.$store.dispatch('auth/setCredentials', { email: this.email, password: this.password });
            return this.$router.push({ name: 'verify-auth-code' });
          }

          window.location = getAuthorizeURL();
        } catch (e) {
          this.submitting = false;
          await this.$addError('Les identifiants entrés sont incorrects');
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .form-input {
    margin-bottom: 3rem;
  }

  .password-input {
    margin-bottom: 1rem;
  }

  .forgotten-password {
    font-size: 1.4rem;
    color: $fadedTextColor;
  }

  .remember-email {
    margin-top: 4rem;
    color: $fadedTextColor;
  }

  .submit {
    margin-top: 15rem;
    text-align: right;
  }

  .registration {
    text-align: right;

    a {
      font-size: 1.5rem;
      color: $textColor;
    }

    p:last-child {
      margin-bottom: 0;
    }
  }

  .title {
    margin-bottom: 1rem;
  }

  .subtitle {
    margin-bottom: 7rem;
  }
</style>
